/**
 * 和暦 (Japanese era-year) formatting for the UI. Mirrors the backend
 * util at backend/src/common/jp/wareki.ts — the frontend can't import
 * backend code, so the era table is duplicated here. Keep them in sync.
 *
 * The era is chosen by the full date (eras change mid-year), then the
 * era-year is `gregorianYear - offset`. Dates are read in UTC so a plain
 * "YYYY-MM-DD" maps to the intended day regardless of timezone.
 */
interface EraDef {
  ja: string;
  /** Inclusive start as an integer YYYYMMDD. */
  from: number;
  offset: number;
}

const ERAS: EraDef[] = [
  { ja: '令和', from: 20190501, offset: 2018 },
  { ja: '平成', from: 19890108, offset: 1988 },
  { ja: '昭和', from: 19261225, offset: 1925 },
  { ja: '大正', from: 19120730, offset: 1911 },
  { ja: '明治', from: 18681023, offset: 1867 },
];

function parseDate(value: string | Date): Date | null {
  const d = value instanceof Date ? value : new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00Z` : value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "令和8年5月29日", or null for an invalid / pre-明治 date. */
export function formatWareki(value: string | Date, opts: { withGannen?: boolean } = {}): string | null {
  const date = parseDate(value);
  if (!date) return null;
  const key = date.getUTCFullYear() * 10000 + (date.getUTCMonth() + 1) * 100 + date.getUTCDate();
  const era = ERAS.find((e) => key >= e.from);
  if (!era) return null;
  const year = date.getUTCFullYear() - era.offset;
  const yearStr = (opts.withGannen ?? true) && year === 1 ? '元' : String(year);
  return `${era.ja}${yearStr}年${date.getUTCMonth() + 1}月${date.getUTCDate()}日`;
}

/**
 * Locale-aware date label: 和暦 when the active language is Japanese,
 * otherwise the original value unchanged. Falls back to the original
 * string for pre-明治 dates that have no era.
 */
export function formatDateForLng(value: string, lng: string): string {
  if (lng?.startsWith('ja')) return formatWareki(value) ?? value;
  return value;
}
